import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import WithSizes from 'react-sizes';
import { useSelector } from 'react-redux';
import { makeStyles, Box } from '@material-ui/core';
import PageResourceSkeleton from 'components/Skeletons/PageResourceSkeleton';
import EmptyState from 'components/EmptyState';
import useSiteCopySelector from 'components/useSiteCopySelector';
import PageMeta from 'components/PageMeta';
import useNavbarElementPosition from 'components/Hooks/useNavbarElementPosition';
import { CourseSidebar } from './CourseSidebar';
import { WelcomeSeries } from './components/WelcomeSeries';
import { CourseReflection } from './components/CourseReflection';
import { CourseBreadcrumbs } from './components/CourseBreadcrumbs';
import { useCourse } from './hooks/useCourse';
import { getTakenCoursesProcessing } from './selectors';
import { COURSE_SIDEBAR_TABS } from './constants';
import CourseIntroduction from './components/CourseIntroduction';
import { CourseNextSteps } from './components/CourseNextSteps';
import { SeriesHeader } from './components/SeriesHeader';
import { CourseRecommended } from './components/CourseRecommended';
import useCourseDashboard from './hooks/useCourseDashboard';
import { CourseWorksheetRenderer } from './components/course-worksheet/CourseWorksheetRenderer';
import { getCourseMeta } from './utils';
import { CourseSeriesProgress } from './components/course-series-progress/CourseSeriesProgress';
import { CourseSurvey } from './components/course-survey/CourseSurvey';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    background: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  rightPanel: {
    width: '100%',
    [theme.breakpoints.between('961', '1200')]: {
      width: 'calc(100% - 300px)',
    },
    [theme.breakpoints.up('1200')]: {
      width: 'calc(100% - 398px)',
    },
    padding: theme.spacing(2),

    [theme.breakpoints.between('961', '1025')]: {
      width: 'calc(100% - 300px)',
      padding: '15px 30px',
    },
    [theme.breakpoints.between('600', '961')]: {
      width: '100%',
      padding: '15px 30px',
    },
  },
  container: {
    '& .article': {
      padding: 0,
    },
  },
}));

const Course = ({ queryInfo, isMobile, isBigTablet, isTablet, match }) => {
  const classes = useStyles();
  const profile = useSelector(state => state.firebase.profile);
  const takenCoursesProcessing = useSelector(getTakenCoursesProcessing);

  const { status, data } = queryInfo;
  const processing = status === 'loading';
  const item = _get(data, 'items.0');
  const trackingPath = 'coursePage';

  const {
    isAuthenticated,
    activeTab,
    activeModule,
    activeSeries,
    activeResource,
    activeModuleIdx,
    activeResourceIdx,
    onResourceClick,
    onSeriesComplete,
    completedResourcesIds,
    seriesProgress,
    handleCompleteChange,
    completeResource,
    handleNext,
    completedModulesIds,
    completedSeriesIds,
    onProgressUpdate,
    resourcesProgress,
    saveAnswer,
    reflectionAnswers,
    onSeriesClick,
    onEnrollCourse,
    completeWelcomeSeries,
    completedAssessments,
    completeSeriesAssessment,
    onRecommendedTabClick,
    saveResourceForLater,
    removeResourceFromLater,
    takenCourse,
    isEnrolled,
    continueCourse,
    saveWorksheetProgress,
    worksheetProgress,
    onModuleClick,
    getNextResourceIndexes,
    goToResource,
    isLastModule,
    surveyType,
    closeSurvey,
  } = useCourse({ item, profile, takenCoursesProcessing, trackingPath });

  const {
    courseProgress,
    completedNumberOfReflections,
    completedNumberOfModules,
    totalNumberOfModules,
    isCourseCompleted,
  } = useCourseDashboard(item);

  const [
    coursesSiteCopy,
    seriesSiteCopy,
    resourceItemPageSiteCopy,
    navbarPageSiteCopy,
    metadataSiteCopy,
  ] = useSiteCopySelector([
    'courses',
    'series',
    'resource-item-page',
    'navbar-menu',
    'metadata',
  ]);

  const navBarPosition = useNavbarElementPosition();

  const rightPanelPaddingRight = `${Math.max(navBarPosition.left, 16)}px`;

  if (!processing && _isEmpty(data.items)) return <EmptyState />;
  if (processing || takenCoursesProcessing) return <PageResourceSkeleton />;

  const isDesktop = !isMobile && !isTablet && !isBigTablet;
  const resourceDescription = _get(activeResource, 'fields.seriesDescription');
  const coursesMeta = _get(metadataSiteCopy, 'pageCopy.courses');
  const pageMeta = getCourseMeta({ item, coursesMeta, activeModule, match });
  const isSeriesComplete =
    !!activeSeries && completedSeriesIds.includes(activeSeries.sys.id);
  const trackingProps = {
    path: trackingPath,
    course: _get(item, 'fields.title'),
    module: _get(activeModule, 'fields.title'),
    section: _get(activeSeries, 'fields.title'),
  };
  const fontSize = isMobile ? '0.875rem' : '1rem';
  const small = isMobile || isTablet;

  const surveyNode = surveyType && (
    <CourseSurvey
      type={surveyType}
      course={item}
      coursesSiteCopy={coursesSiteCopy}
      onClose={closeSurvey}
      small={small}
    />
  );

  if (
    !match.params.path ||
    ['introduction', 'dashboard'].includes(match.params.path)
  ) {
    return (
      <>
        <CourseIntroduction
          isMobile={isMobile}
          onEnrollCourse={onEnrollCourse}
          course={item}
          seriesSiteCopy={seriesSiteCopy}
          coursesSiteCopy={coursesSiteCopy}
          resourceItemPageSiteCopy={resourceItemPageSiteCopy}
          isEnrolled={isEnrolled}
          totalNumberOfModules={totalNumberOfModules}
          completedModulesIds={completedModulesIds}
          completedNumberOfModules={completedNumberOfModules}
          completedNumberOfReflections={completedNumberOfReflections}
          courseProgress={courseProgress}
          removeResourceFromLater={removeResourceFromLater}
          takenCourse={takenCourse}
          isCourseCompleted={isCourseCompleted}
          continueCourse={continueCourse}
          activeModule={activeModule}
          onModuleClick={onModuleClick}
          getNextResourceIndexes={getNextResourceIndexes}
          goToResource={goToResource}
          match={match}
          pageMeta={pageMeta}
          trackingProps={trackingProps}
          fontSize={fontSize}
        />
        {surveyNode}
      </>
    );
  }

  const mapActiveTabToElement = {
    [COURSE_SIDEBAR_TABS.WELCOME_SERIES]: (
      <WelcomeSeries
        item={item}
        series={activeSeries}
        completeWelcomeSeries={completeWelcomeSeries}
        seriesSiteCopy={seriesSiteCopy}
      />
    ),
    [COURSE_SIDEBAR_TABS.RESOURCE]: (
      <CourseSeriesProgress
        activeSeries={activeSeries}
        coursesSiteCopy={coursesSiteCopy}
        seriesSiteCopy={seriesSiteCopy}
        navbarPageSiteCopy={navbarPageSiteCopy}
        completedResourcesIds={completedResourcesIds}
        isAuthenticated={isAuthenticated}
        isDesktop={isDesktop}
        isTablet={isTablet}
        isBigTablet={isBigTablet}
        isMobile={isMobile}
        activeResource={activeResource}
        activeResourceIdx={activeResourceIdx}
        handleCompleteChange={handleCompleteChange}
        handleNext={handleNext}
        completeResource={completeResource}
        onProgressUpdate={onProgressUpdate}
        onSeriesComplete={() => onSeriesComplete()}
        resourcesProgress={resourcesProgress}
        resourceDescription={resourceDescription}
        fontSize={fontSize}
        isSeriesComplete={isSeriesComplete}
        completeSeriesAssessment={completeSeriesAssessment}
        completedAssessments={completedAssessments}
        saveResourceForLater={saveResourceForLater}
        trackingProps={trackingProps}
        small={small}
      />
    ),
    [COURSE_SIDEBAR_TABS.REFLECTION]: (
      <CourseReflection
        course={item}
        series={activeSeries}
        saveAnswer={saveAnswer}
        answers={reflectionAnswers}
        isSeriesComplete={isSeriesComplete}
        onSeriesComplete={onSeriesComplete}
        onNext={handleNext}
        coursesSiteCopy={coursesSiteCopy}
        seriesSiteCopy={seriesSiteCopy}
        activeModule={activeModule}
        trackingPath={trackingPath}
        fontSize={fontSize}
        small={small}
      />
    ),
    [COURSE_SIDEBAR_TABS.NEXT_STEPS]: (
      <CourseNextSteps
        course={item}
        series={activeSeries}
        seriesSiteCopy={seriesSiteCopy}
        coursesSiteCopy={coursesSiteCopy}
        fontSize={fontSize}
        onSeriesComplete={onSeriesComplete}
        isSeriesCompleted={isSeriesComplete}
        isModuleCompleted={completedModulesIds.includes(activeModule.sys.id)}
        isLastModule={isLastModule}
        isCourseCompleted={isCourseCompleted}
        onNext={handleNext}
        activeModule={activeModule}
        trackingProps={trackingPath}
        small={small}
      />
    ),
    [COURSE_SIDEBAR_TABS.RECOMMENDED]: (
      <CourseRecommended
        course={item}
        series={activeSeries}
        coursesSiteCopy={coursesSiteCopy}
        seriesSiteCopy={seriesSiteCopy}
        saveResourceForLater={saveResourceForLater}
        takenCourse={takenCourse}
        onNext={handleNext}
        trackingProps={trackingProps}
        small={small}
      />
    ),
    [COURSE_SIDEBAR_TABS.WORKSHEET]: (
      <CourseWorksheetRenderer
        item={activeResource}
        seriesSiteCopy={seriesSiteCopy}
        coursesSiteCopy={coursesSiteCopy}
        saveWorksheetProgress={saveWorksheetProgress}
        onSeriesComplete={onSeriesComplete}
        completedResourcesIds={completedResourcesIds}
        worksheetProgress={worksheetProgress}
        completeResource={completeResource}
        handleCompleteChange={handleCompleteChange}
        isSeriesComplete={isSeriesComplete}
        onNext={handleNext}
        trackingProps={trackingProps}
        fontSize={fontSize}
        small={small}
      />
    ),
  };

  const sidebarElement = (
    <CourseSidebar
      item={item}
      isMobile={isMobile}
      isTablet={isTablet}
      resourceItemPageSiteCopy={resourceItemPageSiteCopy}
      coursesSiteCopy={coursesSiteCopy}
      activeModule={activeModule}
      activeModuleIdx={activeModuleIdx}
      activeResourceIdx={activeResourceIdx}
      activeSeries={activeSeries}
      onResourceClick={onResourceClick}
      seriesProgress={seriesProgress}
      completedSeriesIds={completedSeriesIds}
      onSeriesClick={onSeriesClick}
      onRecommendedTabClick={onRecommendedTabClick}
      trackingPath={trackingPath}
    />
  );

  return (
    <div className={classes.root}>
      <PageMeta type="courses" data={pageMeta} canonicalUrl={match.url} />
      {(isDesktop || isBigTablet) && sidebarElement}
      <div
        className={classes.rightPanel}
        style={isDesktop ? { paddingRight: rightPanelPaddingRight } : {}}
      >
        <CourseBreadcrumbs
          item={item}
          isEnrolled={isEnrolled}
          activeModule={activeModule}
          match={match}
          small={small}
          coursesSiteCopy={coursesSiteCopy}
        />
        <Box id={`COURSE_${activeTab}`} className={classes.container}>
          <Box display="flex" alignItems="center" mb={isDesktop ? 3 : 2}>
            {small && sidebarElement}
            {activeSeries && (
              <SeriesHeader small={small} activeSeries={activeSeries} />
            )}
          </Box>
          {mapActiveTabToElement[activeTab]}
        </Box>
      </div>
      {surveyNode}
    </div>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width <= 600,
  isBigTablet: width > 960 && width <= 1024,
  isTablet: width > 600 && width <= 960,
});

export default WithSizes(mapSizesToProps)(Course);
